<template>
  <div>
    <DashboardCard
      id="dashboard-availabilities-card"
      :loading="loading"
      :expanded="expanded"
      :widget-type="widgetType"
      title="New Availabilities"
      :count="total"
      @update:expanded="$emit('update:expanded', $event)"
    >
      <template #actions>
        <v-btn
          v-if="$vuetify.breakpoint.mdAndDown"
          icon
          @click="filtersDrawer = true"
        >
          <v-icon>mdi-filter</v-icon>
        </v-btn>
        <DashboardCardFiltersFields
          v-else
          v-model="filters"
          source="availability"
          :default-start-date="defaultStartDate"
          :default-end-date="defaultEndDate"
          :loading="loading"
          :filter-loading="filterLoading"
        />
      </template>
      <template #content>
        <v-skeleton-loader type="table" :loading="loading">
          <DashboardMobileTable
            v-if="$vuetify.breakpoint.mdAndDown"
            :table-options.sync="tableOptions"
            :items="items"
            :filter-loading="filterLoading"
            :total-rows-entry="total"
          >
            <template #item="{ item, index }">
              <AvailabilityMobileCard
                :item="item"
                :active-account-id="activeAccountId"
                :class="index === 0 ? 'mt-0' : 'mt-2'"
              />
            </template>
          </DashboardMobileTable>
          <BaseTable
            v-else
            :table-options-entry="tableOptions"
            :total-rows-entry="total"
            :headers-data-entry="headers"
            :items-entry="items"
            :loading="filterLoading"
            @options:change="tableOptions = $event"
          >
            <template #item="{ item }">
              <ClientContent
                :props="item"
                :allow-select="false"
                :active-account-id="activeAccountId"
                with-tooltip-for-all-brokers
              />
            </template>
          </BaseTable>
        </v-skeleton-loader>
      </template>
    </DashboardCard>
    <DashboardCardFilters
      v-if="$vuetify.breakpoint.mdAndDown"
      v-model="filters"
      source="availability"
      title="New Availabilities Filters"
      :open.sync="filtersDrawer"
      :default-start-date="defaultStartDate"
      :default-end-date="defaultEndDate"
      :loading="loading"
      :filter-loading="filterLoading"
    />
  </div>
</template>

<script lang="ts">
import mixins from 'vue-typed-mixins';
import { isEqual } from 'lodash-es';
import { mapState } from 'vuex';

import { addDates } from '@/utils/filters';
import { ServiceFactory } from '@/services/ServiceFactory';
import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { getTableOptions } from '@/bundles/BaseTable/helpers/consts';
import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { AvailabilitiesCardMixin } from '@/bundles/Availabilities/mixins/AvailabilitiesCardMixin';
import { getAvailabilityClientHeadersData } from '@/bundles/Availabilities/helpers';
import { IDashboardCardFilters } from '@/bundles/Dashboard/interfaces';
import { getFormattedGroupFiltersArray } from '@/bundles/GroupFilters/helpers';
import { mapOldFiltersFromSetToNew } from '@/bundles/GroupFilters/helpers/mappers/mapOldFiltersFromSetToNew';
import { AllExcludeOnlyEnum } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { PROPERTY_TYPE_KEY } from '@/bundles/GroupFilters/consts';

import BaseTable from '@/bundles/BaseTable/components/BaseTable.vue';
import DashboardCard from '@/bundles/Dashboard/components/DashboardCard.vue';
import ClientContent from '@/bundles/Availabilities/components/content-view/ClientContent.vue';
import DashboardMobileTable from '@/bundles/Dashboard/components/DashboardMobileTable.vue';
import AvailabilityMobileCard from '@/bundles/Availabilities/components/cards/AvailabilityMobileCard.vue';
import DashboardCardFilters from '@/bundles/Dashboard/components/filters/DashboardCardFilters.vue';
import DashboardCardFiltersFields from '@/bundles/Dashboard/components/filters/DashboardCardFiltersFields.vue';

const tableOptions = getTableOptions({
  itemsPerPage: 10,
  sortBy: ['created.date'],
  sortDesc: [true]
});

const AvailabilityService = ServiceFactory.get('availability');
interface IDashboardAvailabilitiesCardData {
  widgetType: UserWidgetsEnum;
  tableOptions: ITableOptions;
  isFirstLoad: boolean;
  filters: IDashboardCardFilters;
  filtersDrawer: boolean;
  filterParams: Record<string, any>;
}

export default mixins(AvailabilitiesCardMixin).extend({
  name: 'DashboardAvailabilitiesCard',

  components: {
    AvailabilityMobileCard,
    DashboardMobileTable,
    BaseTable,
    DashboardCard,
    ClientContent,
    DashboardCardFilters,
    DashboardCardFiltersFields
  },
  props: {
    defaultStartDate: {
      type: String,
      default: ''
    },
    defaultEndDate: {
      type: String,
      default: ''
    },
    expanded: {
      type: Boolean,
      default: true
    },
    sourceId: {
      type: String,
      default: ''
    },
    sourceType: {
      type: String,
      default: ''
    }
  },

  data: (): IDashboardAvailabilitiesCardData => ({
    widgetType: UserWidgetsEnum.new_availabilities,
    tableOptions,
    isFirstLoad: true,
    filters: {
      inHouse: true,
      dateStart: addDates(new Date(Date.now()).toISOString(), { days: -14, }),
      dateEnd: new Date().toISOString().substr(0, 10),
      propertyType: null,
      savedSet: null,
    },
    filtersDrawer: false,
    filterParams: {},
  }),

  computed: {
    ...mapState({
      activeAccountId: (state: any) => state.activeAccount.id
    }),
    ...mapState('Settings', {
      opexDisplay: (state: any) => state.opexDisplay,
    }),
    headers (): IHeader[] {
      return [
        { text: '#', value: 'number', sortable: false },
        ...getAvailabilityClientHeadersData(this.opexDisplay)
      ];
    },
  },
  watch: {
    tableOptions: {
      handler: function () {
        this.fetchAvailabilities();
      },
      deep: true,
      immediate: true,
    },
    filters: {
      handler: function () {
        this.fetchAvailabilities();
      }
    }
  },
  methods: {
    async fetchAvailabilities () {
      try {
        this.filterLoading = true;
        // values must match the group filters wire format: date_range and multiple_select filters are arrays,
        // and the in-house filter is present only when restricting ('all' means no restriction on the list page)
        const cardFilters: Record<string, any> = {
          availability_view_date: [this.filters.dateStart, this.filters.dateEnd],
        };
        if (this.filters.inHouse) {
          cardFilters.availability_in_house = AllExcludeOnlyEnum.only;
        }
        if (this.filters.propertyType) {
          cardFilters[PROPERTY_TYPE_KEY] = [this.filters.propertyType];
        }

        // no view_type: the previous GET request didn't send one and its response shape
        // is consumed by both ClientContent (desktop) and AvailabilityMobileCard (mobile)
        const params: Record<string, any> = {
          pageOffset: this.pageOffset,
          pageLimit: this.tableOptions.itemsPerPage,
          sort: this.tableOptions.sortBy,
          descending: this.tableOptions.sortDesc,
          filters: [cardFilters],
        };

        if (this.filters.savedSet) {
          if (this.filters.savedSet.class === 'static') {
            params.setId = this.filters.savedSet.savedSet._key;
            params.filters = [{ ...cardFilters, setId: this.filters.savedSet.savedSet._key }];
          } else {
            params.savedSet = this.filters.savedSet.savedSet._key;
            const setGroups = getFormattedGroupFiltersArray(
              mapOldFiltersFromSetToNew(this.filters.savedSet.filter_groups || [], 'availability')
            );
            // groups are OR'ed by the api, so the card's own filters are merged into each group to keep AND semantics
            params.filters = setGroups.length
              ? setGroups.map((group) => ({ ...group, ...cardFilters }))
              : [cardFilters];
          }
        }
        if (isEqual(this.filterParams, params)) {
          return;
        }

        if (!this.isFirstLoad) {
          await this.$vuetify.goTo('#dashboard-availabilities-card');
        }

        this.filterParams = { ...params };
        const { data } = await AvailabilityService.listGroupFilters({ token: this.token, params });
        this.items = data.result;
        this.total = data.total;
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.loading = false;
        this.filterLoading = false;
        this.isFirstLoad = false;
      }
    }
  }
})
</script>
